import React, { useState, useEffect } from 'react';
import { Plus, Edit, Search, Download, UserPlus, Calendar, X, MessageSquare, Eye, Repeat } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function KardexLicenciatura() {
    const [asignaturas, setAsignaturas] = useState([]);
    const [asignaciones, setAsignaciones] = useState([]);
    const [carreras, setCarreras] = useState([]);
    const [menciones, setMenciones] = useState([]);
    const [docentes, setDocentes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Filtros
    const [filtroGestion, setFiltroGestion] = useState(new Date().getFullYear());
    const [filtroCarrera, setFiltroCarrera] = useState('');
    const [filtroMencion, setFiltroMencion] = useState('');
    const [filtroNivel, setFiltroNivel] = useState('');

    // Modales
    const [showModal, setShowModal] = useState(false);
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [filtroSala, setFiltroSala] = useState('');
    const [filtroDocente, setFiltroDocente] = useState('');
    const [filtroDia, setFiltroDia] = useState('');
    const [selectedObservation, setSelectedObservation] = useState(null);

    const [selectedAsignatura, setSelectedAsignatura] = useState(null);
    const [formData, setFormData] = useState({
        id_docente: '',
        paralelo: '',
        capacidad: 30,
        dias: [],
        color: '#3B82F6',
        horarios_detalle: {}
    });

    // Modal Homologación
    const [showHomologarModal, setShowHomologarModal] = useState(false);
    const [homologarData, setHomologarData] = useState({
        id_asignatura: '',
        nuevo_nombre: '',
        nuevo_codigo: ''
    });

    const gestiones = [2023, 2024, 2025, 2026, 2027, 2028];
    const niveles = [
        { value: 1, label: 'Primer Año' },
        { value: 2, label: 'Segundo Año' },
        { value: 3, label: 'Tercer Año' },
        { value: 4, label: 'Cuarto Año' }
    ];
    const diasSemana = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
    const horasDisponibles = [];
    for (let h = 6; h <= 18; h++) {
        horasDisponibles.push(`${h.toString().padStart(2, '0')}:00`);
        horasDisponibles.push(`${h.toString().padStart(2, '0')}:30`);
    }

    useEffect(() => {
        loadData();
    }, [filtroGestion, filtroCarrera, filtroMencion, filtroNivel]);

    const loadData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('gestion', filtroGestion);
            if (filtroCarrera) params.append('id_carrera', filtroCarrera);
            if (filtroMencion) params.append('id_mencion', filtroMencion);
            if (filtroNivel) params.append('nivel', filtroNivel);

            const [asignaturasRes, carrerasRes, mencionesRes, docentesRes, asignacionesRes] = await Promise.all([
                fetch(`${API_URL}/jefatura/asignaturas?${params.toString()}`),
                fetch(`${API_URL}/jefatura/carreras`),
                fetch(`${API_URL}/jefatura/menciones`),
                fetch(`${API_URL}/jefatura/docentes`),
                fetch(`${API_URL}/jefatura/asignaciones-horario?gestion=${filtroGestion}`)
            ]);

            const asignaturasData = await asignaturasRes.json();
            const carrerasData = await carrerasRes.json();
            const mencionesData = await mencionesRes.json();
            const docentesData = await docentesRes.json();
            const asignacionesData = await asignacionesRes.json();

            // Filtrar solo asignaturas activas para la lista principal
            const listAsig = (asignaturasData.asignaturas || []).filter(a => a.is_active !== false);

            setAsignaturas(listAsig);
            setCarreras(carrerasData.carreras || []);
            setMenciones(mencionesData.menciones || []);
            setDocentes(docentesData.docentes || []);
            setAsignaciones(asignacionesData.asignaciones || []);
        } catch (error) {
            console.error('Error cargando datos:', error);
        } finally {
            setLoading(false);
        }
    };

    const getAsignaciones = (idAsignatura) => {
        return asignaciones.filter(a =>
            parseInt(a.id_asignatura) === parseInt(idAsignatura) &&
            parseInt(a.gestion) === parseInt(filtroGestion)
        );
    };

    const getDocenteNombre = (id) => {
        if (!id || !docentes) return '-';
        const d = docentes.find(doc => doc && parseInt(doc.id_docente) === parseInt(id));
        return (d && d.nombres) ? `${d.nombres} ${d.apellidos}` : '-';
    };

    const getNivelTexto = (nivel) => {
        const n = niveles.find(l => l.value === nivel);
        return n ? n.label : `Año ${nivel}`;
    };

    const asignaturasFiltradas = (() => {
        if (!searchTerm || searchTerm.trim() === '') {
            return asignaturas;
        }

        const search = searchTerm.toLowerCase().trim();
        return asignaturas.filter(a => {
            const codigo = (a.codigo || '').toLowerCase();
            const nombre = (a.nombre || '').toLowerCase();
            const nivel = getNivelTexto(a.nivel).toLowerCase();

            if (codigo.includes(search) || nombre.includes(search) || nivel.includes(search)) return true;

            const assignments = getAsignaciones(a.id_asignatura);
            return assignments.some(asig => {
                const docenteName = getDocenteNombre(asig.id_docente).toLowerCase();
                const sala = (asig.sala || '').toLowerCase();
                const paralelo = (asig.paralelo || '').toLowerCase();
                return docenteName.includes(search) || sala.includes(search) || paralelo.includes(search);
            });
        });
    })();

    const handleAsignarDocente = (asignatura) => {
        setSelectedAsignatura(asignatura);
        setFormData({
            id_docente: '',
            paralelo: '',
            capacidad: 30,
            dias: [],
            color: '#3B82F6',
            horarios_detalle: {},
            id_asignacion: null
        });
        setShowModal(true);
    };

    const handleEditarParalelo = (asignatura, asignacion) => {
        setSelectedAsignatura(asignatura);
        let horariosDetalle = {};
        if (asignacion.horarios_detalle && Array.isArray(asignacion.horarios_detalle)) {
            asignacion.horarios_detalle.forEach(d => {
                horariosDetalle[d.dia] = {
                    hora_inicio: d.hora_inicio || '',
                    hora_fin: d.hora_fin || '',
                    sala: d.sala || ''
                };
            });
        }
        setFormData({
            id_docente: asignacion.id_docente || '',
            paralelo: asignacion.paralelo || '',
            capacidad: asignacion.capacidad || 30,
            dias: asignacion.dias || [],
            color: asignacion.color || '#3B82F6',
            horarios_detalle: horariosDetalle,
            id_asignacion: asignacion.id_asignacion
        });
        setShowModal(true);
    };

    const handleGuardarAsignacion = async () => {
        if (!formData.id_docente || !formData.paralelo || formData.dias.length === 0) {
            alert('Complete todos los campos obligatorios');
            return;
        }
        try {
            const isEdit = !!formData.id_asignacion;
            const url = isEdit ? `${API_URL}/jefatura/asignaciones-horario/${formData.id_asignacion}` : `${API_URL}/jefatura/asignaciones-horario`;
            const horariosDetalle = formData.dias.map(dia => ({
                dia,
                hora_inicio: formData.horarios_detalle[dia]?.hora_inicio || '',
                hora_fin: formData.horarios_detalle[dia]?.hora_fin || '',
                sala: formData.horarios_detalle[dia]?.sala || ''
            }));

            const res = await fetch(url, {
                method: isEdit ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_asignatura: selectedAsignatura.id_asignatura,
                    gestion: filtroGestion,
                    id_docente: formData.id_docente,
                    paralelo: formData.paralelo.toUpperCase(),
                    capacidad: formData.capacidad,
                    dias: formData.dias,
                    color: formData.color,
                    horarios_detalle: horariosDetalle
                })
            });

            if (res.ok) {
                setShowModal(false);
                loadData();
            } else {
                const err = await res.json();
                alert(err.message || 'Error al guardar');
            }
        } catch (error) {
            alert('Error de conexión');
        }
    };

    const handleHomologar = async () => {
        if (!homologarData.id_asignatura || !homologarData.nuevo_nombre) {
            alert('Complete el nombre de la materia homologada');
            return;
        }
        try {
            const res = await fetch(`${API_URL}/jefatura/asignaturas/homologar`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(homologarData)
            });
            if (res.ok) {
                alert('Materia(s) homologada(s) exitosamente');
                setShowHomologarModal(false);
                loadData();
            } else {
                const err = await res.json();
                alert(err.message || 'Error al homologar');
            }
        } catch (error) {
            alert('Error al conectar con el servidor');
        }
    };

    const toggleDia = (dia) => {
        setFormData(prev => {
            const newDias = prev.dias.includes(dia) ? prev.dias.filter(d => d !== dia) : [...prev.dias, dia];
            const newDetalle = { ...prev.horarios_detalle };
            if (!prev.dias.includes(dia)) newDetalle[dia] = { hora_inicio: '', hora_fin: '', sala: '' };
            else delete newDetalle[dia];
            return { ...prev, dias: newDias, horarios_detalle: newDetalle };
        });
    };

    const handleDetalleChange = (dia, field, value) => {
        setFormData(prev => ({
            ...prev,
            horarios_detalle: { ...prev.horarios_detalle, [dia]: { ...prev.horarios_detalle[dia], [field]: value } }
        }));
    };

    const [showNewAsigModal, setShowNewAsigModal] = useState(false);
    const [editingSubject, setEditingSubject] = useState(null);
    const [newAsigData, setNewAsigData] = useState({
        codigo: '', nombre: '', horas: 4, id_carrera: '', id_mencion: '', nivel: 1, prerequisito_codigo: ''
    });

    const openNewAsigModal = () => {
        setEditingSubject(null);
        setNewAsigData({ codigo: '', nombre: '', horas: 4, id_carrera: filtroCarrera, id_mencion: filtroMencion, nivel: filtroNivel || 1, prerequisito_codigo: '' });
        setShowNewAsigModal(true);
    };

    const handleCreateAsignatura = async () => {
        try {
            const isEdit = !!editingSubject;
            const url = isEdit ? `${API_URL}/jefatura/asignaturas/${editingSubject.id_asignatura}` : `${API_URL}/jefatura/asignaturas`;
            const res = await fetch(url, {
                method: isEdit ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newAsigData)
            });
            if (res.ok) {
                setShowNewAsigModal(false);
                loadData();
            } else {
                const err = await res.json();
                alert(err.message || 'Error al guardar');
            }
        } catch (e) { alert('Error de conexión'); }
    };

    const handleDownloadExcel = () => {
        const rows = asignaturasFiltradas.flatMap((a, idx) => {
            const asigs = getAsignaciones(a.id_asignatura);
            if (asigs.length === 0) return [{ No: idx + 1, Año: `${a.nivel}°`, Sigla: a.codigo, Asignatura: a.nombre, Docente: '-', Estado: 'Sin asignar' }];
            return asigs.map(p => ({
                No: idx + 1, Año: `${a.nivel}°`, Sigla: a.codigo, Asignatura: a.nombre,
                Docente: getDocenteNombre(p.id_docente), Paralelo: p.paralelo,
                Horario: (p.horarios_detalle || []).map(d => `${d.dia}: ${d.hora_inicio}-${d.hora_fin}`).join(' | '),
                Estado: 'Asignado'
            }));
        });
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Listado');
        XLSX.writeFile(wb, `Reporte_Licenciatura_${filtroGestion}.xlsx`);
    };

    if (loading) return <div className="p-12 text-center text-blue-900 font-bold">Cargando datos de Kardex...</div>;

    return (
        <div className="p-6">
            <div className="mb-6 flex justify-between items-center">
                <div className="mb-6">
                    <h1 className="text-xl font-bold text-gray-800">Kardex &gt; Homologación Asignaturas &gt; Licenciatura</h1>
                </div>
                <div className="flex gap-3">
                    <button onClick={openNewAsigModal} className="flex items-center gap-2 px-4 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition shadow-md">
                        <Plus size={18} /> Nueva Asignatura
                    </button>
                    <button onClick={() => { setShowHomologarModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-md">
                        <Repeat size={18} /> HOMOLOGACIÓN ASIGNATURAS
                    </button>
                </div>
            </div>

            {/* Filtros */}
            {/* <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6 bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">Gestión</label>
                    <select value={filtroGestion} onChange={e => setFiltroGestion(e.target.value)} className="w-full border-gray-200 rounded-xl text-sm focus:ring-blue-500">
                        {gestiones.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                </div>
                <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">Carrera</label>
                    <select value={filtroCarrera} onChange={e => setFiltroCarrera(e.target.value)} className="w-full border-gray-200 rounded-xl text-sm focus:ring-blue-500">
                        <option value="">Todas</option>
                        {carreras.filter(c => [8, 9, 10].includes(c.id_carrera)).map(c => <option key={c.id_carrera} value={c.id_carrera}>{c.nombre}</option>)}
                    </select>
                </div>
                <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">Nivel</label>
                    <select value={filtroNivel} onChange={e => setFiltroNivel(e.target.value)} className="w-full border-gray-200 rounded-xl text-sm focus:ring-blue-500">
                        <option value="">Todos</option>
                        {niveles.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
                    </select>
                </div>
                <div className="md:col-span-2 space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">Búsqueda Rápida</label>
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                        <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Materia, código, docente..." className="w-full pl-10 border-gray-200 rounded-xl text-sm focus:ring-blue-500" />
                    </div>
                </div>
            </div> */}

            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">

                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label>
                        <select
                            value={filtroGestion} onChange={e => setFiltroGestion(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            {gestiones.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                        <span className="absolute right-3 top-[30px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>
                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">CARRERA</label>
                        <select
                            value={filtroCarrera} onChange={e => setFiltroCarrera(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            <option value="">Todas</option>
                            {carreras.filter(c => [8, 9, 10].includes(c.id_carrera)).map(c => <option key={c.id_carrera} value={c.id_carrera}>{c.nombre}</option>)}
                        </select>
                        <span className="absolute right-3 top-[30px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>
                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">NIVEL</label>
                        <select
                            value={filtroNivel} onChange={e => setFiltroNivel(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            <option value="">Todos</option>
                            {niveles.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
                        </select>
                        <span className="absolute right-3 top-[30px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>
                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Materia, código, docente..."
                                     value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* Tabla */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                        <tr>
                            <th className="px-3 py-3 text-center font-semibold border">ID</th>
                            <th className="px-3 py-3 text-center font-semibold border">Año</th>
                            <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                            <th className="px-3 py-3 text-center font-semibold border">Docentes / Paralelos</th>
                            <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {asignaturasFiltradas.map((a, idx) => {
                            const asigs = getAsignaciones(a.id_asignatura);
                            return (
                                <tr key={a.id_asignatura} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 text-center border">{idx + 1}</td>
                                    <td className="px-3 py-2 text-center border font-medium"><span >{a.nivel}° año</span></td>
                                    <td className="px-3 py-2 border">
                                        <div className="font-bold text-gray-800">{a.nombre}</div>
                                        <div className="text-[10px] text-gray-400 font-mono">{a.codigo} | {a.horas} hrs</div>
                                        {a.id_original && a.id_original !== a.id_asignatura && (
                                            <span className="mt-1 inline-block bg-emerald-100 text-emerald-700 text-[9px] px-1.5 rounded-full font-bold uppercase">Homologada</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 border">
                                        {asigs.length === 0 ? <span className="text-gray-300 italic text-xs">Sin asignación</span> : (
                                            <div className="flex flex-wrap gap-2">
                                                {asigs.map(p => (
                                                    <div key={p.id_asignacion} onClick={() => handleEditarParalelo(a, p)} className="bg-white border border-gray-200 rounded-lg p-2 cursor-pointer hover:border-blue-400 transition min-w-[120px]">
                                                        <div className="text-[10px] font-bold text-blue-600">Paralelo {p.paralelo}</div>
                                                        <div className="text-[11px] truncate font-medium">{getDocenteNombre(p.id_docente)}</div>
                                                        <div className="text-[9px] text-gray-400 mt-1">{p.dias?.map(d => d.substring(0, 2)).join(', ')}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text- border">
                                        <div className="flex justify-center gap-2">
                                            <button onClick={() => handleAsignarDocente(a)} className="p-2 rounded-full transition shadow-sm bg-blue-100 text-blue-600 hover:bg-blue-200" title="Asignar paralelo">
                                                <Plus size={18} />
                                            </button>
                                            <button onClick={() => { setEditingSubject(a); setNewAsigData(a); setShowNewAsigModal(true); }} className="p-1.5 rounded-full transition shadow-sm bg-yellow-100 text-yellow-600 hover:bg-yellow-200" title="Editar">
                                                <Edit size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>

            {/* Modal Homologación */}
            {showHomologarModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b">
                            {/* <Repeat size={40} className="mx-auto mb-2 opacity-80" /> */}
                            <h2 className="text-xl font-bold text-blue-900">Homologar Materia</h2>
                            {/* <p className="text-emerald-100 text-xs">Se creará una nueva versión de la materia conservando la historia de la anterior.</p> */}
                        </div>
                        <div className="p-8 space-y-4">
                            <div className="space-y-1">
                                <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1 uppercase">MATERIA ACTUAL (UNIFICADA POR NOMBRE)</label>
                                <select
                                    className="w-full px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                                    value={homologarData.id_asignatura}
                                    onChange={e => {
                                        const asig = asignaturas.find(a => a.id_asignatura == e.target.value);
                                        setHomologarData({ ...homologarData, id_asignatura: e.target.value, nuevo_nombre: asig?.nombre || '', nuevo_codigo: asig?.codigo || '' });
                                    }}
                                >
                                    <option value="">Seleccione materia...</option>
                                    {/* Agrupar por nombre único */}
                                    {Array.from(new Set(asignaturas.map(a => a.nombre)))
                                        .sort()
                                        .map(nombreUnico => {
                                            const asigRef = asignaturas.find(a => a.nombre === nombreUnico);
                                            const count = asignaturas.filter(a => a.nombre === nombreUnico).length;
                                            return (
                                                <option key={asigRef.id_asignatura} value={asigRef.id_asignatura}>
                                                    {nombreUnico} ({count} {count === 1 ? 'carrera' : 'carreras'})
                                                </option>
                                            );
                                        })}
                                </select>
                                <p className="text-[10px] text-gray-400 italic">El cambio afectará a todas las carreras que tengan una materia con este nombre.</p>
                            </div>
                            <div className="space-y-1">
                                <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1 uppercase">NUEVO NOMBRE (HOMOLOGADO)</label>
                                <input
                                    type="text"
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500 uppercase"
                                    value={homologarData.nuevo_nombre}
                                    onChange={e => setHomologarData({ ...homologarData, nuevo_nombre: e.target.value.toUpperCase() })}
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1 uppercase">NUEVO CÓDIGO (OPCIONAL)</label>
                                <input
                                    type="text"
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500 uppercase"
                                    value={homologarData.nuevo_codigo}
                                    onChange={e => setHomologarData({ ...homologarData, nuevo_codigo: e.target.value.toUpperCase() })}
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button onClick={() => setShowHomologarModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">CANCELAR</button>
                                <button onClick={handleHomologar} className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800">HOMOLOGAR</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Nueva Asignatura (Reutilizado) */}
            {showNewAsigModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-3xl shadow-xl w-full max-w-lg p-6">
                        <div className="flex items-center justify-between pb-4 border-b">
                            <h3 className="text-xl font-bold text-blue-900">{editingSubject ? 'Editar Asignatura' : 'Nueva Asignatura'}</h3>
                            <button onClick={() => setShowNewAsigModal(false)} className="text-gray-400"><X size={24} /></button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">Nombre</label>
                                <input type="text" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500 uppercase" value={newAsigData.nombre} onChange={e => setNewAsigData({ ...newAsigData, nombre: e.target.value.toUpperCase() })} />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">Código</label>
                                <input type="text" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500 uppercase" value={newAsigData.codigo} onChange={e => setNewAsigData({ ...newAsigData, codigo: e.target.value.toUpperCase() })} />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">Nivel</label>
                                <select className="w-full px-4 py-2 border border-gray-300 rounded-full bg-white text-sm pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" value={newAsigData.nivel} onChange={e => setNewAsigData({ ...newAsigData, nivel: parseInt(e.target.value) })}>
                                    {niveles.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">Carrera</label>
                                <select className="w-full px-4 py-2 border border-gray-300 rounded-full bg-white text-sm pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 " value={newAsigData.id_carrera} onChange={e => setNewAsigData({ ...newAsigData, id_carrera: e.target.value })}>
                                    <option value="">Seleccionar...</option>
                                    {carreras.filter(c => [8, 9, 10].includes(c.id_carrera)).map(c => <option key={c.id_carrera} value={c.id_carrera}>{c.nombre}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <button onClick={() => setShowNewAsigModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">CANCELAR</button>
                            <button onClick={handleCreateAsignatura} className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg hover:bg-[#2c4a9a] disabled:opacity-50">{editingSubject ? 'ACTUALIZAR' : 'CREAR'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Asignar Horario (Simplificado para el ejemplo, pero completo en lógica) */}
            {showModal && selectedAsignatura && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg w-full max-w-xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="text-xl font-bold text-blue-900">Configurar Horario y Paralelo</h3>
                            <button onClick={() => setShowModal(false)} className="text-gray-400"><X size={24} /></button>
                        </div>
                        <div className="grid grid-cols-2 gap-6 m-6">
                            <div className="col-span-2 bg-blue-50 p-4 rounded-2xl border border-blue-100">
                                <div className="text-xs text-blue-500 font-bold uppercase mb-1">Materia Seleccionada</div>
                                <div className="font-bold text-blue-900">{selectedAsignatura.nombre}</div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">Docente</label>
                                <select className="w-full border-gray-200 rounded-xl" value={formData.id_docente} onChange={e => setFormData({ ...formData, id_docente: e.target.value })}>
                                    <option value="">Seleccionar...</option>
                                    {docentes.map(d => <option key={d.id_docente} value={d.id_docente}>{d.nombres} {d.apellidos}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">Paralelo</label>
                                    <input type="text" className="w-full border-gray-200 rounded-xl font-bold text-center" value={formData.paralelo} onChange={e => setFormData({ ...formData, paralelo: e.target.value.toUpperCase().slice(0, 2) })} placeholder="A" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">Capacidad</label>
                                    <input type="number" className="w-full border-gray-200 rounded-xl" value={formData.capacidad} onChange={e => setFormData({ ...formData, capacidad: parseInt(e.target.value) })} />
                                </div>
                            </div>
                            <div className="col-span-2">
                                <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1 mb-3 block">Días de Clase</label>
                                <div className="space-y-3">
                                    {diasSemana.map(dia => {
                                        const isActive = formData.dias.includes(dia);
                                        const det = formData.horarios_detalle[dia] || {};
                                        return (
                                            <div key={dia} className={`p-4 rounded-2xl border ${isActive ? 'border-blue-500 bg-blue-50/50' : 'border-gray-100'}`}>
                                                <label className="flex items-center gap-3 cursor-pointer">
                                                    <input type="checkbox" checked={isActive} onChange={() => toggleDia(dia)} className="rounded-full text-blue-600 focus:ring-blue-500" />
                                                    <span className={`font-bold text-sm ${isActive ? 'text-blue-900' : 'text-gray-400'}`}>{dia}</span>
                                                </label>
                                                {isActive && (
                                                    <div className="grid grid-cols-3 gap-2 mt-3">
                                                        {/* <select className="text-xs border-gray-200 rounded-lg" value={det.hora_inicio} onChange={e => handleDetalleChange(dia, 'hora_inicio', e.target.value)}>
                                                            <option value="">Inicio</option>
                                                            {horasDisponibles.map(h => <option key={h} value={h}>{h}</option>)}
                                                        </select>
                                                        <select className="text-xs border-gray-200 rounded-lg" value={det.hora_fin} onChange={e => handleDetalleChange(dia, 'hora_fin', e.target.value)}>
                                                            <option value="">Fin</option>
                                                            {horasDisponibles.map(h => <option key={h} value={h}>{h}</option>)}
                                                        </select> */}



                                                        {/* Inicio */}
                                                        <div className="flex flex-col gap-1">
                                                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                                                Hora Inicio
                                                            </label>
                                                            <input
                                                                type="time"
                                                                className="text-xs border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none w-full"
                                                                value={det.hora_inicio || ''}
                                                                onChange={e => handleDetalleChange(dia, 'hora_inicio', e.target.value)}
                                                            />
                                                        </div>

                                                        {/* Flecha indicadora */}
                                                        {/* <span className="text-gray-400 mb-2.5">→</span> */}

                                                        {/* Fin */}
                                                        <div className="flex flex-col gap-1">
                                                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                                                Hora Fin
                                                            </label>
                                                            <input
                                                                type="time"
                                                                className="text-xs border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none w-full"
                                                                value={det.hora_fin || ''}
                                                                onChange={e => handleDetalleChange(dia, 'hora_fin', e.target.value)}
                                                                min={det.hora_inicio} // Validación nativa del navegador
                                                            />
                                                        </div>
                                                        <div className="flex flex-col gap-1">
                                                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                                                Sala
                                                            </label>
                                                            <select className="text-xs border-gray-200 rounded-lg h-8" value={det.sala} onChange={e => handleDetalleChange(dia, 'sala', e.target.value)}>
                                                                <option value="">Sala</option>
                                                                {Array.from({ length: 15 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
                                                            </select>
                                                        </div>


                                                        {/* <select className="text-xs border-gray-200 rounded-lg" value={det.sala} onChange={e => handleDetalleChange(dia, 'sala', e.target.value)}>
                                                            <option value="">Sala</option>
                                                            {Array.from({ length: 15 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
                                                        </select> */}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-4 m-6">
                            <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">CANCELAR</button>
                            <button onClick={handleGuardarAsignacion} className="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50">GUARDAR</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}